/**
 * Infringement type + reason options — kept in sync with
 * collector_extension/content/infringementReasons.js and
 * dashboard/src/webapp/src/utils/labels.js.
 */

import {
  InfringementReason,
  InfringementType,
  ListingState,
  type BrandTraxListing,
} from '../types/brandtraxListing';

export const INFRINGEMENT_TYPES: { text: string; value: InfringementType }[] = [
  { text: 'Copyright', value: InfringementType.CR },
  { text: 'Trademark', value: InfringementType.TM },
  { text: 'Design', value: InfringementType.DE },
  { text: 'Counterfeit', value: InfringementType.CFT },
  { text: 'Cybersquatting', value: InfringementType.CS },
];

export interface DistributionReasonEntry {
  value: string;
  text: string;
  classification: 'INFRINGING' | 'NON_INFRINGING';
}

export const DISTRIBUTION_REASON_OPTIONS: DistributionReasonEntry[] = [
  {
    value: 'POLICY_REGULATORY_VIOLATION',
    text: 'Policy/Regulatory Violation',
    classification: 'INFRINGING',
  },
  {
    value: 'SAMPLE_TESTER_SALE',
    text: 'Sample/Tester Sale',
    classification: 'INFRINGING',
  },
  {
    value: 'SELECTIVE_INTEGRATED_DISTRIBUTION',
    text: 'Selective/Integrated Distribution',
    classification: 'INFRINGING',
  },
  {
    value: 'SALE_OF_EXPIRED_PRODUCTS',
    text: 'Sale of Expired Products',
    classification: 'INFRINGING',
  },
  {
    value: 'SOLD_WITHOUT_PRESCRIPTION',
    text: 'Sold without Prescription',
    classification: 'INFRINGING',
  },
  {
    value: 'PARALLEL_IMPORT',
    text: 'Parallel Import',
    classification: 'NON_INFRINGING',
  },
  { value: 'NO_RISK', text: 'No Risk', classification: 'NON_INFRINGING' },
];

export interface InfringementReasonEntry {
  value: InfringementReason;
  toi: InfringementType;
  text: string;
}

/** Same ordering as collector_extension infringement dropdown. */
export const INFRINGEMENT_REASONS: InfringementReasonEntry[] = [
  { value: InfringementReason.COUNTERFEIT, toi: InfringementType.CFT, text: 'Counterfeit' },
  {
    value: InfringementReason.CUSTOMIZED_SAMPLES,
    toi: InfringementType.CFT,
    text: 'Customized Samples',
  },
  {
    value: InfringementReason.CONCEALED_TRADEMARK,
    toi: InfringementType.CFT,
    text: 'Concealed Trademark',
  },
  {
    value: InfringementReason.LOW_PRICE_PRODUCT,
    toi: InfringementType.CFT,
    text: 'Low Price Product',
  },
  { value: InfringementReason.ODD_VOLUME_SIZE, toi: InfringementType.CFT, text: 'Odd Volume Size' },
  {
    value: InfringementReason.PRODUCT_NOT_MANUFACTURED,
    toi: InfringementType.CFT,
    text: 'Product Not Manufactured',
  },
  {
    value: InfringementReason.NOT_FOR_SALE_SAMPLE,
    toi: InfringementType.CFT,
    text: 'Not For Sale Sample',
  },
  {
    value: InfringementReason.HIGH_RISK_KEYWORDS,
    toi: InfringementType.CFT,
    text: 'High Risk Keywords',
  },
  {
    value: InfringementReason.VARIATION_IN_TRADE_DRESS,
    toi: InfringementType.CFT,
    text: 'Variation in Trade Dress',
  },
  { value: InfringementReason.FAKE_TESTERS, toi: InfringementType.CFT, text: 'Fake Testers' },
  {
    value: InfringementReason.FAKE_OR_MISLEADING_APP,
    toi: InfringementType.CFT,
    text: 'Fake or Misleading App',
  },
  {
    value: InfringementReason.UNAUTHORIZED_MARK,
    toi: InfringementType.TM,
    text: 'Unauthorized mark',
  },
  {
    value: InfringementReason.UNAUTHORIZED_LOGO,
    toi: InfringementType.TM,
    text: 'Unauthorized logo',
  },
  {
    value: InfringementReason.UNAUTHORIZED_DEVICE,
    toi: InfringementType.TM,
    text: 'Unauthorized device',
  },
  {
    value: InfringementReason.REG_COPYRIGHT_MATCH,
    toi: InfringementType.CR,
    text: 'Registered copyright use',
  },
  {
    value: InfringementReason.COPYRIGHT_MATCH,
    toi: InfringementType.CR,
    text: 'Use of copyright image',
  },
  {
    value: InfringementReason.DESIGN_INFRINGEMENT,
    toi: InfringementType.DE,
    text: 'Design patent infringement',
  },
  {
    value: InfringementReason.UTILITY_INFRINGEMENT,
    toi: InfringementType.DE,
    text: 'Utility patent infringement',
  },
  {
    value: InfringementReason.LOGO_CHARACTER_MATCH,
    toi: InfringementType.CR,
    text: 'Use of logo/character',
  },
  {
    value: InfringementReason.PACK_SHOT_IMAGE,
    toi: InfringementType.CR,
    text: 'Use of official packshots',
  },
  {
    value: InfringementReason.CREATIVE_VISUAL_IMAGE,
    toi: InfringementType.CR,
    text: 'Use of Creative Visuals',
  },
];

/** Classification values shown on review submit (matches collector extension). */
export const REVIEW_CLASSIFICATION_OPTIONS: { value: ListingState; label: string }[] = [
  { value: ListingState.NEW, label: 'New' },
  { value: ListingState.INFRINGING, label: 'Infringing' },
  { value: ListingState.UNAUTHORIZED_DISTRIBUTION, label: 'Unauthorized Distribution' },
  { value: ListingState.SUSPECT, label: 'Suspect' },
  { value: ListingState.LOOKALIKE, label: 'Lookalike' },
  { value: ListingState.IRRELEVANT, label: 'Irrelevant' },
];

export function getInfringementTypeLabel(toi: InfringementType): string {
  const hit = INFRINGEMENT_TYPES.find(t => t.value === toi);
  return hit ? hit.text : '';
}

export function getInfringementReasonEntry(
  reasonValue: string | undefined | null
): InfringementReasonEntry | null {
  if (!reasonValue) return null;
  return INFRINGEMENT_REASONS.find(r => r.value === reasonValue) || null;
}

/** Menu label: "Trademark : Unauthorized mark" */
export function formatInfringementOptionLabel(entry: InfringementReasonEntry): string {
  return `${getInfringementTypeLabel(entry.toi)} : ${entry.text}`;
}

export function getInfringementSelectOptions(): { value: InfringementReason; label: string }[] {
  return INFRINGEMENT_REASONS.map(r => ({
    value: r.value,
    label: formatInfringementOptionLabel(r),
  }));
}

export interface BrandInfringementOptionsResponse {
  brand?: string;
  types?: { value: string; label: string }[];
  reasons?: { value: string; toi: string; label: string }[];
  distributionReasons?: {
    value: string;
    label: string;
    classification: string;
  }[];
}

/** Map store API distribution reasons to select options. */
export function distributionOptionsToSelectOptions(
  options: BrandInfringementOptionsResponse | null | undefined
): { value: string; label: string }[] {
  if (!options?.distributionReasons?.length) {
    return DISTRIBUTION_REASON_OPTIONS.map(r => ({ value: r.value, label: r.text }));
  }
  return options.distributionReasons.map(r => ({
    value: r.value,
    label: r.label,
  }));
}

export function allowedDistributionValuesFromOptions(
  options: BrandInfringementOptionsResponse | null | undefined
): Set<string> | undefined {
  if (!options?.distributionReasons) {
    return undefined;
  }
  return new Set(options.distributionReasons.map(r => r.value));
}

export function getDistributionReasonEntry(
  value: string | undefined | null
): DistributionReasonEntry | null {
  if (!value) return null;
  return DISTRIBUTION_REASON_OPTIONS.find(r => r.value === value) || null;
}

/** Map store API response to collector-style select options. */
export function infringementOptionsToSelectOptions(
  options: BrandInfringementOptionsResponse | null | undefined
): { value: string; label: string }[] {
  if (!options?.reasons?.length) {
    return [];
  }
  return options.reasons.map(r => {
    const typeLabel = getInfringementTypeLabel(r.toi as InfringementType);
    const label = typeLabel ? `${typeLabel} : ${r.label}` : r.label;
    return { value: r.value, label };
  });
}

export function allowedReasonValuesFromOptions(
  options: BrandInfringementOptionsResponse | null | undefined
): Set<string> | undefined {
  if (!options?.reasons) {
    return undefined;
  }
  return new Set(options.reasons.map(r => r.value));
}

/**
 * Set listing.reason and listing.toi from a reason enum value.
 * Applies TM→CFT conversion when reason is COUNTERFEIT (importer issue #90).
 */
export function applyInfringementToListing(
  listing: BrandTraxListing,
  reasonValue: string
): void {
  const entry = getInfringementReasonEntry(reasonValue);
  if (!entry) return;
  let toi = entry.toi;
  if (toi === InfringementType.TM && entry.value === InfringementReason.COUNTERFEIT) {
    toi = InfringementType.CFT;
  }
  listing.reason = entry.value;
  listing.toi = toi;
}

export function applyClassificationToBrandTraxListing(
  listing: BrandTraxListing,
  state: ListingState,
  reasonValue?: string
): BrandTraxListing {
  const result: BrandTraxListing = { ...listing, state };
  if (state === ListingState.INFRINGING && reasonValue) {
    applyInfringementToListing(result, reasonValue);
    delete result.distributionReason;
  } else if (state === ListingState.UNAUTHORIZED_DISTRIBUTION && reasonValue) {
    result.distributionReason = reasonValue;
    delete result.reason;
    delete result.toi;
  } else {
    delete result.reason;
    delete result.toi;
    delete result.distributionReason;
  }
  return result;
}

export function validateClassificationBeforeSubmit(
  state: ListingState,
  reasonValue: string,
  allowedReasons?: Set<string>,
  allowedDistributionReasons?: Set<string>
): string | null {
  if (state === ListingState.INFRINGING) {
    const reason = reasonValue.trim();
    if (!reason) {
      return 'Select an infringement type and reason when classification is Infringing.';
    }
    if (allowedReasons && allowedReasons.size > 0 && !allowedReasons.has(reason)) {
      return 'Selected infringement is not available for this brand.';
    }
    if (!getInfringementReasonEntry(reason)) {
      return 'Invalid infringement selection.';
    }
    return null;
  }
  if (state === ListingState.UNAUTHORIZED_DISTRIBUTION) {
    const reason = reasonValue.trim();
    if (!reason) {
      return 'Select an unauthorized distribution reason.';
    }
    if (
      allowedDistributionReasons &&
      allowedDistributionReasons.size > 0 &&
      !allowedDistributionReasons.has(reason)
    ) {
      return 'Selected distribution reason is not available for this brand.';
    }
    if (!getDistributionReasonEntry(reason)) {
      return 'Invalid distribution reason selection.';
    }
  }
  return null;
}

export const APPLY_AI_INFRINGEMENT_STORAGE_KEY = 'scraperUiApplyAiInfringement';

export function canApplyAiInfringementForState(state: ListingState): boolean {
  return (
    state !== ListingState.INFRINGING &&
    state !== ListingState.UNAUTHORIZED_DISTRIBUTION
  );
}

export function loadApplyAiInfringementPreference(): boolean {
  try {
    return localStorage.getItem(APPLY_AI_INFRINGEMENT_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function saveApplyAiInfringementPreference(value: boolean): void {
  try {
    localStorage.setItem(
      APPLY_AI_INFRINGEMENT_STORAGE_KEY,
      value ? 'true' : 'false'
    );
  } catch {
    // ignore storage errors
  }
}

export function buildAnalysisImportOptions(
  state: ListingState,
  applyAiInfringement: boolean
): { applyAiInfringement: boolean } | undefined {
  if (!canApplyAiInfringementForState(state) || !applyAiInfringement) {
    return undefined;
  }
  return { applyAiInfringement: true };
}
